import { useQuery } from '@tanstack/react-query'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { fetchDocuments } from '@/api/documents'
import { FileText, Download, ExternalLink, Wrench, Network, Shield, Lock } from 'lucide-react'
import { cn, formatDate } from '@/lib/utils'

const productAreas = [
  { id: 'SASE', name: 'SASE', icon: Network },
  { id: 'Network Services', name: 'Network Services', icon: Wrench },
  { id: 'Application Security', name: 'Application Security', icon: Shield },
  { id: 'Zero Trust', name: 'Zero Trust', icon: Lock },
]

export function Implementation() {
  const { data: documentsData, isLoading } = useQuery({
    queryKey: ['implementation-documents'],
    queryFn: () => fetchDocuments('implementation'),
  })

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="h-32 animate-pulse rounded-lg bg-muted" />
        <div className="grid gap-4 md:grid-cols-2">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-48 animate-pulse rounded-lg bg-muted" />
          ))}
        </div>
      </div>
    )
  }

  const documents = documentsData?.documents || []

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold">Implementation Resources</h1>
        <p className="text-muted-foreground">
          Technical guides, architecture diagrams, and deployment playbooks
        </p>
      </div>

      {/* Product Area Tabs */}
      <Tabs defaultValue="all" className="space-y-4">
        <TabsList className="flex flex-wrap">
          <TabsTrigger value="all">All Areas</TabsTrigger>
          {productAreas.map((area) => (
            <TabsTrigger key={area.id} value={area.id}>
              <area.icon className="mr-2 h-4 w-4" />
              {area.name}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="all">
          <DocumentsList documents={documents} />
        </TabsContent>

        {productAreas.map((area) => (
          <TabsContent key={area.id} value={area.id}>
            <DocumentsList 
              documents={documents.filter((d: any) => d.product_area === area.id)} 
            />
          </TabsContent>
        ))}
      </Tabs>
    </div>
  )
}

function DocumentsList({ documents }: { documents: any[] }) {
  if (documents.length === 0) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center justify-center p-12 text-center">
          <FileText className="h-12 w-12 text-muted-foreground" />
          <h3 className="mt-4 text-lg font-medium">No documents yet</h3>
          <p className="mt-2 text-sm text-muted-foreground">
            Implementation guides will be added here soon.
          </p>
        </CardContent>
      </Card>
    )
  }

  return (
    <div className="grid gap-4 md:grid-cols-2">
      {documents.map((doc) => (
        <Card key={doc.id} className="card-hover">
          <CardHeader className="pb-3">
            <div className="flex items-start justify-between">
              <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-cloudflare-orange/10">
                <FileText className="h-6 w-6 text-cloudflare-orange" />
              </div>
              <Badge variant="secondary">{doc.product_area || 'General'}</Badge>
            </div>
            <CardTitle className="text-lg">{doc.filename}</CardTitle>
            <CardDescription>
              Added {formatDate(doc.uploaded_at)} • {(doc.size / 1024).toFixed(1)} KB
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                className="flex-1"
                asChild
              >
                <a href={doc.signed_url} target="_blank" rel="noopener noreferrer">
                  <ExternalLink className="mr-2 h-4 w-4" />
                  View
                </a>
              </Button>
              <Button 
                variant="secondary"
                asChild
              >
                <a href={doc.signed_url} download>
                  <Download className="h-4 w-4" />
                </a>
              </Button>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  )
}
